import React from 'react';
import * as yup from 'yup';
import { Button, Flex, FormControl, FormErrorMessage, FormLabel, Input, LightMode, Stack } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { UnderlineLink } from './chakraUIPro/UnderlineLink';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { useAuthDispatch } from '../contexts/AuthContext';

type LoginFormProps = {
  callback(err: string | null, user?: { username: string; password: string }): void;
};

function SignInForm({ callback }: LoginFormProps) {
  const { logEvent } = useAnalytics();
  const { signIn, dispatch } = useAuthDispatch();
  const loginSchema = yup.object({
    email: yup.string().email('Must be a valid email').required('Email is required'),
    password: yup.string().required('Password is required')
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const onSubmit = async (evt: any | undefined) => {
    const { email, password } = evt;
    const { isSignedIn, nextStep } = await signIn({ email, password });

    // Forced password reset on new account login
    if (!isSignedIn && nextStep?.signInStep) {
      callback(nextStep?.signInStep, { username: email, password });
      return;
    }

    logEvent({
      action: 'signed_in',
      category: 'Sign in'
    });
  };

  async function onForgotPassword() {
    dispatch({ type: 'resetPassword' });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing='-1px'>
        {/* Email */}
        <FormControl isInvalid={!!errors.email}>
          <FormLabel srOnly htmlFor='email'>
            Email
          </FormLabel>
          <Controller
            name='email'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id='email'
                size='lg'
                type='email'
                autoComplete='email'
                required
                placeholder='Email address'
                bg='white'
                fontSize='md'
                roundedBottom='0'
                borderBottomColor='transparent'
                focusBorderColor='orange.300'
              />
            )}
          />
          <FormErrorMessage>{errors.email && errors.email.message}</FormErrorMessage>
        </FormControl>

        {/* Password */}
        <FormControl isInvalid={!!errors.password}>
          <FormLabel srOnly htmlFor='password'>
            Password
          </FormLabel>
          <Controller
            name='password'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id='password'
                type='password'
                autoComplete='current-password'
                required
                size='lg'
                bg='white'
                fontSize='md'
                roundedTop='0'
                placeholder='Password'
                focusBorderColor='orange.300'
              />
            )}
          />
          <FormErrorMessage>{errors.password && errors.password.message}</FormErrorMessage>
        </FormControl>
      </Stack>

      <Flex align='center' justify='flex-end' mt='8'>
        <UnderlineLink onClick={onForgotPassword}>Forgot Password?</UnderlineLink>
      </Flex>

      <LightMode>
        <Button
          size='lg'
          type='submit'
          mt='8'
          w='full'
          colorScheme='orange'
          fontSize='md'
          fontWeight='bold'
          isLoading={isSubmitting}
          isDisabled={Object.keys(errors).length > 0}
        >
          Sign in
        </Button>
      </LightMode>
    </form>
  );
}

export default SignInForm;
